import { useEffect, useState } from 'react'
import { defaultUrlTransform } from 'react-markdown'
import { resolveVaultNode, getVaultFileObjectUrl } from '@/lib/api'
import { useVaultStore } from '@/stores/useVaultStore'

// Link schemes that keep default browser handling (open in a new tab). Anything
// else with an href is treated as a vault-relative link to a node — the agent
// emits these as `[label](<Domain/Workstream/.../note.md>)` (2026-06-14 format),
// and they appear both in chat messages and inside rendered block/card bodies.
const EXTERNAL_LINK_SCHEME = /^(https?|mailto|message|tel|obsidian):/i

/**
 * URL sanitizer for ReactMarkdown that PRESERVES the schemes this system relies
 * on — `message://` (links back to the source email in Mail.app, the whole
 * point of saving them) and `obsidian://` — which react-markdown's default
 * transform strips as "unsafe", leaving the link as dead text. Everything else
 * defers to the safe default (still blocks `javascript:` etc.), and
 * vault-relative links (no scheme) pass through for MarkdownVaultAnchor to
 * resolve. Pass to EVERY ReactMarkdown that renders block/card/chat content:
 * `<ReactMarkdown urlTransform={vaultUrlTransform} …>`.
 */
export function vaultUrlTransform(url: string): string {
  if (/^(message|obsidian):/i.test(url)) return url
  return defaultUrlTransform(url)
}

// Image srcs the browser can load as-is — leave them untouched.
const RESOLVABLE_IMG_SRC = /^(https?:|data:|blob:)/i

/**
 * ReactMarkdown image renderer for vault-embedded images. Card/block bodies
 * embed images as markdown links to a vault path (`![alt](<Walker/.../x.png>)`)
 * which the browser can't load off the web root. Fetch the bytes through the
 * authenticated vault-file endpoint and render the resulting blob URL.
 * Use as `components={{ img: MarkdownVaultImage }}`.
 */
export function MarkdownVaultImage({ src, alt }: { src?: string; alt?: string }) {
  const [resolved, setResolved] = useState<string | undefined>(
    !src || RESOLVABLE_IMG_SRC.test(src) ? src : undefined
  )
  useEffect(() => {
    if (!src || RESOLVABLE_IMG_SRC.test(src)) {
      setResolved(src)
      return
    }
    let cancelled = false
    let url: string | null = null
    getVaultFileObjectUrl(src)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u)
          return
        }
        url = u
        setResolved(u)
      })
      .catch(() => {})
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [src])
  return <img src={resolved} alt={alt ?? ''} />
}

/**
 * Resolve a vault-relative link target to a node and open it in the /vault
 * center pane. Folder-note links resolve to their folder node, and archived /
 * title-drifted blocks resolve via the server's timestamp fallback. No-ops
 * gracefully if the path isn't in the projection.
 */
export async function openVaultLink(href: string) {
  let path = href
  try {
    path = decodeURIComponent(href)
  } catch {
    /* keep raw */
  }
  const node = await resolveVaultNode(path)
  if (!node) return
  const st = useVaultStore.getState()
  if (node.parent_id) st.expand(node.parent_id)
  st.select(node.id)
}

/**
 * Anchor for ReactMarkdown-rendered content. Vault-relative links open the
 * target node in the center pane (instead of navigating the SPA to a non-route
 * → blank screen / react-router "No routes matched"). True external links keep
 * default new-tab behavior. Use as `components={{ a: MarkdownVaultAnchor }}`.
 */
export function MarkdownVaultAnchor({
  href,
  children,
}: {
  href?: string
  children?: React.ReactNode
}) {
  const url = href ?? ''
  const isExternal = EXTERNAL_LINK_SCHEME.test(url)
  if (isExternal || !url || url.startsWith('#')) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        {children}
      </a>
    )
  }
  return (
    <a
      href={url}
      className="cursor-pointer"
      onClick={(e) => {
        e.preventDefault()
        void openVaultLink(url)
      }}
    >
      {children}
    </a>
  )
}
